// ArticleA6.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const THEME = {
  fontFamily:
    "'Prompt', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#0b1220",
  textOnDark: "rgba(255,255,255,.92)",
  glass: "rgba(255,255,255,.08)",
  glassStroke: "rgba(255,255,255,.18)",
  primary: "#60a5fa",
  primary2: "#22d3ee",
};

const META = {
  id: "a6",
  title: "ข้อตกลงก่อนสมรส (Prenup) จำเป็นเมื่อไร และต้องระบุอะไรบ้าง?",
  category: "ครอบครัว",
  dateISO: "2025-08-26",
  readMinsFallback: 7,
  hero:
    "https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=2000&auto=format&fit=crop",
  excerpt:
    "สรุปเหตุผลที่ควรทำ Prenup หัวข้อสำคัญที่ห้ามพลาด ขั้นตอนเตรียมเอกสาร และเทมเพลตโครงสัญญา/ภาคผนวกทรัพย์สินที่ก๊อปไปปรับใช้ได้",
};

export default function ArticleA6() {
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [readMins, setReadMins] = useState(META.readMinsFallback);
  const contentRef = useRef(null);
  const heroRef = useRef(null);

  // progress + hero parallax
  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const total = el.scrollHeight - window.innerHeight * 0.6;
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - window.innerHeight * 0.4), 0),
        total
      );
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);

      const h = heroRef.current;
      if (h) {
        const y = window.scrollY;
        h.style.setProperty("--parallax", Math.min(y * 0.22, 120) + "px");
        h.style.setProperty("--blur", Math.min(y / 70, 8) + "px");
        h.style.setProperty("--fade", Math.min(y / 420, 0.5));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // compute reading time
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const words = (el.innerText || "").trim().split(/\s+/).length;
    setReadMins(Math.max(4, Math.round(words / 200)));
  }, []);

  // ScrollSpy
  useEffect(() => {
    const heads = document.querySelectorAll("main article h2[id], main article h3[id]");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id)),
      { rootMargin: "0px 0px -70% 0px", threshold: 0.01 }
    );
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, []);

  const url = useMemo(
    () => (typeof window !== "undefined" ? window.location.href : "/articles/a6"),
    []
  );

  const share = (type) => {
    const text = encodeURIComponent(META.title);
    const u = encodeURIComponent(url);
    let shareUrl = "";
    if (type === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    if (type === "tw") shareUrl = `https://twitter.com/intent/tweet?url=${u}&text=${text}`;
    if (type === "ln") shareUrl = `https://social-plugins.line.me/lineit/share?url=${u}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=680,height=520");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert("คัดลอกลิงก์แล้ว");
    } catch {
      alert("คัดลอกไม่สำเร็จ");
    }
  };
  const copyTemplate = (id, label) => {
    const t = document.getElementById(id)?.innerText || "";
    navigator.clipboard
      .writeText(t)
      .then(() => alert(`คัดลอก${label}แล้ว`))
      .catch(() => alert("คัดลอกไม่สำเร็จ"));
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div className="article-page" style={{ fontFamily: THEME.fontFamily }}>
      <Style />
      <ReadingProgress value={progress} />

      {/* SEO JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: META.title,
            datePublished: META.dateISO,
            dateModified: META.dateISO,
            author: { "@type": "Person", name: "ทีมทนายความ Thai Tax & Law" },
            image: META.hero,
            articleSection: META.category,
            description: META.excerpt,
            mainEntityOfPage: url,
          }),
        }}
      />

      {/* HERO */}
      <header className="hero" ref={heroRef}>
        <img className="hero-media" src={META.hero} alt="" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <nav className="breadcrumbs">
            <a href="/">หน้าแรก</a> <span>•</span> <a href="/articles">บทความ</a>{" "}
            <span>•</span> <span aria-current="page">{META.category}</span>
          </nav>
        <h1 className="hero-title">{META.title}</h1>
          <p className="hero-excerpt">{META.excerpt}</p>
          <div className="hero-meta">
            <span>{fmtDate(META.dateISO)}</span>
            <span>•</span>
            <span>{readMins} นาที</span>
            <span className="cat">{META.category}</span>
          </div>
        </div>
        <div className="hero-bottom-fade" />
      </header>

      {/* LAYOUT */}
      <div className="containerr">
        {/* SIDE */}
        <aside className="side tools">
          <div className="tool-card">
            <div className="tool-title">สารบัญ</div>
            <nav className="toc">
              {[
                ["intro", "ภาพรวม & ประโยชน์ของ Prenup"],
                ["why", "ควรทำเมื่อไร / ไม่จำเป็นเมื่อไร"],
                ["law", "พื้นฐานกฎหมายไทยที่ควรรู้"],
                ["content", "หัวข้อสำคัญที่ควรระบุ"],
                ["process", "ขั้นตอนเตรียมและลงนาม"],
                ["templates", "โครงสัญญา (ก๊อปได้)"],
                ["exhibit", "ภาคผนวกทรัพย์สิน (ตัวอย่าง)"],
                ["tips", "คุยกับคู่ชีวิตอย่างไรดี"],
                ["faq", "FAQ"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={activeId === id ? "active" : ""}
                  onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(id);
                if (el) {
                    const yOffset = -80; // ชดเชย navbar 80px
                    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
                }}

                >
                  {label}
                </a>
              ))}
            </nav>
          </div>

          
        </aside>

        {/* MAIN */}
        <main>
          <article ref={contentRef}>
            <p className="lede">
              เป้าหมายของ <b>Prenup</b> ไม่ใช่ “ไม่ไว้ใจกัน” แต่คือการ<b>จัดระบบทรัพย์สินและหนี้</b> ให้ชัด
              ลดความเสี่ยงธุรกิจ และทำให้การตัดสินใจเรื่องเงินในชีวิตคู่โปร่งใสตั้งแต่แรก
            </p>

            <Callout type="info">
              บทความนี้เป็นข้อมูลทั่วไป ไม่ใช่คำปรึกษาเฉพาะเคส กฎเกณฑ์ทางกฎหมายอาจมีการปรับปรุง
              ก่อนใช้งานจริงควรตรวจสอบระเบียบล่าสุดหรือติดต่อ <a href="/contact">ทีมทนายความของเรา</a>
            </Callout>

            <h2 id="intro">ภาพรวม & ประโยชน์ของ Prenup</h2>
            <ul className="check">
              <li>กำหนดขอบเขต <b>สินส่วนตัว / สินสมรส</b> ชัดตั้งแต่ต้น</li>
              <li>วางวิธีจัดการ <b>หนี้ส่วนบุคคล / หนี้ธุรกิจ</b></li>
              <li>คุ้มครอง <b>กิจการ/หุ้น/ทรัพย์มรดก</b> ของครอบครัว</li>
              <li>ลดข้อพิพาทหากต้องแยกทางในอนาคต</li>
            </ul>

            <h2 id="why">ควรทำเมื่อไร / ไม่จำเป็นเมื่อไร</h2>
            <div className="grid-2">
              <div>
                <h3>เหมาะอย่างยิ่งเมื่อ</h3>
                <ul className="check">
                  <li>มี <b>ทรัพย์สิน/ธุรกิจ</b> ก่อนสมรสหรือมีหนี้เดิม</li>
                  <li>ครอบครัวให้ <b>มรดก/ของขวัญเฉพาะตัว</b></li>
                  <li>มีคู่สมรสต่างสัญชาติหรือมี <b>ทรัพย์สินต่างประเทศ</b></li>
                </ul>
              </div>
              <div>
                <h3>อาจไม่จำเป็นเมื่อ</h3>
                <ul className="check">
                  <li>ทรัพย์สินน้อย ไม่มีหนี้ และทั้งคู่ยอมรับ <b>กติกากฎหมายปกติ</b></li>
                  <li>ไม่มีความเสี่ยงธุรกิจ/ค้ำประกัน</li>
                </ul>
              </div>
            </div>

            <h2 id="law">พื้นฐานกฎหมายไทยที่ควรรู้</h2>
            <ul>
              <li><b>ควรทำเป็นลายลักษณ์อักษร และยื่นแนบทะเบียนสมรสในวันจดทะเบียน</b> เพื่อให้มีผลสมบูรณ์ตามกฎหมาย</li>
              <li>เนื้อหาไม่ควรขัดต่อกฎหมาย/ความสงบเรียบร้อยหรือศีลธรรมอันดี</li>
              <li>หลังสมรส การแก้ไขเงื่อนไขโดยทั่วไปทำได้จำกัดและอาจต้องผ่านกระบวนการทางกฎหมาย</li>
            </ul>

            <h2 id="content">หัวข้อสำคัญที่ควรระบุ</h2>
            <ol className="steps">
              <li><b>คำจำกัดความ & การเปิดเผยทรัพย์สิน</b> (Asset Disclosure)</li>
              <li><b>การจัดประเภททรัพย์</b> — สินส่วนตัว/สินสมรส/ดอกผล</li>
              <li><b>การจัดการหนี้</b> — หนี้ส่วนบุคคล/หนี้ธุรกิจ/หนี้ร่วม</li>
              <li><b>ธุรกิจ/หุ้น</b> — สิทธิออกเสียง/เงินปันผล/การโอนหากเลิกกัน</li>
              <li><b>ที่อยู่อาศัย</b> — สิทธิอาศัย/ค่าใช้จ่าย/แนวทางเมื่อแยกกันอยู่</li>
              <li><b>บัญชีการเงิน</b> — บัญชีส่วนตัว/บัญชีร่วม/วิธีบริหารค่าใช้จ่ายครอบครัว</li>
              <li><b>ของขวัญ/มรดก</b> — เงื่อนไขรักษาสถานะ “เฉพาะตัว”</li>
              <li><b>ข้อกำหนดทั่วไป</b> — ทางเลือกข้อพิพาท/กฎหมายที่ใช้บังคับ</li>
            </ol>

            <h2 id="process">ขั้นตอนเตรียมและลงนาม</h2>
            <div className="grid-2">
              <ul className="check">
                <li>ทำ <b>บัญชีทรัพย์สิน/หนี้</b> ของแต่ละฝ่าย</li>
                <li>เปิดเผยข้อมูลสำคัญอย่างสุจริต (income, ภาระหนี้)</li>
                <li>ร่างเอกสาร + <b>อ่านโดยที่ปรึกษาแยกกันของแต่ละฝ่าย</b> (ถ้าเป็นไปได้)</li>
              </ul>
              <ul className="check">
                <li>จัดทำ <b>ภาคผนวกทรัพย์สิน</b> แนบท้าย</li>
                <li>เตรียมเวอร์ชันสองภาษา (ถ้าจำเป็น)</li>
                <li><b>ลงนามและยื่นแนบทะเบียน</b> ณ วันที่จดทะเบียนสมรส</li>
              </ul>
            </div>

            <h2 id="templates">โครงสัญญา (ก๊อปได้)</h2>
            <div className="template">
              <pre id="prenup-template">{`ข้อตกลงก่อนสมรส (Prenuptial Agreement)

คู่สัญญา
1) นาย/นาง/น.ส. ______________________________ ("ฝ่าย ก")
2) นาย/นาง/น.ส. ______________________________ ("ฝ่าย ข")

1. คำจำกัดความและการเปิดเผยทรัพย์สิน
ทั้งสองฝ่ายได้เปิดเผยทรัพย์สินและหนี้สินตามความเป็นจริง รายละเอียดตามภาคผนวกแนบท้าย

2. สินส่วนตัวและสินสมรส
2.1 ทรัพย์สินที่มีอยู่ก่อนสมรสและทรัพย์ที่ได้รับโดยเฉพาะตัว ให้ถือเป็นสินส่วนตัวของผู้ได้รับ
2.2 ดอกผลของสินส่วนตัวให้ถือเป็น _________ (ระบุ: สินส่วนตัว/สินสมรส)
2.3 ทรัพย์ที่ได้มาระหว่างสมรส ให้ถือเป็นสินสมรส เว้นแต่จะระบุเป็นอย่างอื่นไว้ชัดแจ้ง

3. หนี้สิน
3.1 หนี้ก่อนสมรสเป็นความรับผิดของฝ่ายที่ก่อหนี้
3.2 หนี้เพื่อประโยชน์ครอบครัว/กิจการร่วม ให้ถือเป็นหนี้ร่วมตามสมควร
3.3 หนี้ธุรกิจเฉพาะบุคคล ให้เป็นความรับผิดของผู้ก่อหนี้ เว้นแต่ตกลงเป็นหนังสือ

4. ธุรกิจ/หลักทรัพย์
สิทธิออกเสียง การโอนหุ้น เงินปันผล และการชดเชยมูลค่าเมื่อเลิกกัน ให้เป็นไปตามข้อกำหนดในภาคผนวกธุรกิจ

5. ที่อยู่อาศัย
กำหนดสิทธิการอยู่อาศัย/ค่าใช้จ่าย/แนวทางจัดการเมื่อเลิกกัน เช่น การขายและแบ่ง หรือชดเชยมูลค่า

6. บัญชีการเงิน
กำหนดบัญชีส่วนตัว/บัญชีร่วม วิธีแบ่งค่าใช้จ่ายประจำ และวิธีเปลี่ยนแปลงวงเงิน

7. ของขวัญ/มรดก
ทรัพย์ที่ได้รับโดยเสน่หาเฉพาะตัว/มรดก ให้ถือเป็นสินส่วนตัวของผู้ได้รับ เว้นแต่ทำหนังสือระบุเป็นอย่างอื่น

8. การระงับข้อพิพาทและกฎหมายที่ใช้บังคับ
กำหนดขั้นตอนไกล่เกลี่ย/อนุญาโตตุลาการ (ถ้าเหมาะสม) และกฎหมายที่ใช้บังคับ

ลงชื่อ _____________________  ฝ่าย ก        วันที่ ___/___/____
ลงชื่อ _____________________  ฝ่าย ข        วันที่ ___/___/____

พยาน 1 _____________________               พยาน 2 _____________________`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("prenup-template", "โครงสัญญา Prenup")}>คัดลอกโครงสัญญา</button>
                <a className="ghost" href="/contact">ให้ทีมเราปรับตามเคสของคุณ</a>
              </div>
            </div>

            <h2 id="exhibit">ภาคผนวกทรัพย์สิน (ตัวอย่าง)</h2>
            <div className="template">
              <pre id="exhibit-template">{`ภาคผนวก A — บัญชีทรัพย์สินและหนี้ (ณ วันที่ก่อนสมรส)

ฝ่าย ก
- เงินฝาก/หลักทรัพย์: ธนาคาร___ เลขบัญชี (ลงท้าย) ___ ยอดประมาณ __________
- อสังหาริมทรัพย์: โฉนดเลขที่ ___ มูลค่าประเมิน __________ (แนบเอกสาร)
- ธุรกิจ/หุ้น: บริษัท ___ สัดส่วนถือหุ้น ___%
- หนี้สิน: เงินกู้เลขที่ ___ ยอดคงค้าง __________

ฝ่าย ข
- เงินฝาก/หลักทรัพย์: __________________________
- อสังหาริมทรัพย์: _____________________________
- ธุรกิจ/หุ้น: __________________________________
- หนี้สิน: _____________________________________

บันทึก: คู่สัญญาตกลงว่า รายการข้างต้นเป็นการเปิดเผยโดยสุจริต และจะปรับปรุงรายการหากมีการเปลี่ยนแปลงอย่างมีนัยสำคัญก่อนวันสมรส`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("exhibit-template", "ภาคผนวกทรัพย์สิน")}>คัดลอกภาคผนวก</button>
              </div>
            </div>

            <h2 id="tips">คุยกับคู่ชีวิตอย่างไรดี</h2>
            <ul className="check">
              <li>เริ่มจาก <b>เป้าหมายร่วม</b> (ความมั่นคง/แผนครอบครัว) ไม่ใช่การป้องกันตัว</li>
              <li>แยกการคุย “หลักการ” ออกจาก “รายละเอียดเอกสาร”</li>
              <li>ให้เวลาทบทวน/ขอความเห็นจากที่ปรึกษาอิสระ</li>
            </ul>

            <h2 id="faq">FAQ</h2>
            <details>
              <summary>หลังสมรสแล้ว ยังทำหรือแก้ไขได้ไหม?</summary>
              <p>โดยทั่วไปการแก้ไขหลังสมรสทำได้จำกัดและอาจต้องผ่านกระบวนการทางกฎหมาย ควรวางแผนและยื่นแนบในวันจดทะเบียน</p>
            </details>
            <details>
              <summary>ต้องทำสองภาษาไหมถ้าคู่สมรสเป็นชาวต่างชาติ?</summary>
              <p>แนะนำให้ทำสองภาษาเพื่อความเข้าใจตรงกัน และควรให้ผู้แปล/ทนายความตรวจถ้อยคำสำคัญ</p>
            </details>
            <details>
              <summary>ต้องมีพยานกี่คน/ต้องโนตารีไหม?</summary>
              <p>ปฏิบัติตามข้อกำหนดของทางการในวันที่ยื่น แนบกับทะเบียนสมรสให้ครบ และเก็บสำเนาไว้ทั้งสองฝ่าย</p>
            </details>

            <Callout type="success">
              ต้องการทีมร่าง Prenup แบบสองภาษา + ตรวจธุรกิจ/ทรัพย์สินประกอบ?{" "}
              <a href="/contact">นัดคุยฟรี 15 นาที</a> กับทีมคดีครอบครัวของเรา
            </Callout>

            <div className="end-actions">
              <a className="btn-primary" href="/contact">
                ปรึกษาทีมทนายความตอนนี้
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M5 12h12M13 5l7 7-7 7" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a
                className="ghost"
                href="#top"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                กลับขึ้นบน
              </a>
            </div>
          </article>

          {/* RELATED */}
          <section className="related">
            <h3>อ่านต่อ</h3>
            <div className="rel-grid">
              <a className="rel-card" href="/articles/a2">
                <img src="https://www.lawdepot.com/images/lawLibrary/Articles/are-divorce-records-public.jpg" alt="" />
                <div className="meta">
                  <span className="cat">ครอบครัว</span>
                  <div className="title">วางแผนทรัพย์สินก่อนการหย่า: ปกป้องสิทธิ</div>
                </div>
              </a>
              <a className="rel-card" href="/articles/a3">
                <img src="https://images.unsplash.com/photo-1542621334-a254cf47733d?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">สัญญา</span>
                  <div className="title">สัญญากู้ยืมเงิน: เขียนให้ฟ้องได้</div>
                </div>
              </a>
              <a className="rel-card" href="/articles/a5">
                <img src="https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">ทวงหนี้</span>
                  <div className="title">Checklist ทวงหนี้ธุรกิจรายเดือน</div>
                </div>
              </a>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

/* ---------- Components ---------- */
function ReadingProgress({ value }) {
  return (
    <div className="progress-wrap" aria-hidden="true">
      <div className="progress" style={{ width: `${value}%` }} />
    </div>
  );
}
function Callout({ type = "info", children }) {
  return <div className={`callout ${type}`}>{children}</div>;
}

/* ---------- Styles ---------- */
function Style() {
  return (
    <style>{`
:root{
  --bg:${THEME.pageBg};
  --text:${THEME.textOnDark};
  --glass:${THEME.glass};
  --glass-stroke:${THEME.glassStroke};
  --p1:${THEME.primary};
  --p2:${THEME.primary2};
}
*{box-sizing:border-box}
.article-page{
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(14,165,233,.12), transparent 60%),
    radial-gradient(1200px 600px at 90% 10%, rgba(99,102,241,.12), transparent 60%),
    var(--bg);
  min-height:100vh;
}

/* progress */
.progress-wrap{position:fixed;top:0;left:0;right:0;height:3px;z-index:100}
.progress{height:3px;background:linear-gradient(90deg,var(--p1),var(--p2));box-shadow:0 0 16px rgba(34,211,238,.35)}

/* hero */
.hero{position:relative;height:58vh;overflow:hidden;isolation:isolate;--parallax:0px;--blur:0px;--fade:0}
.hero-media{position:absolute;inset:-10% -10% -10% -10%;width:120%;height:120%;object-fit:cover;transform:translateY(var(--parallax));filter:blur(var(--blur)) brightness(.85) saturate(.95)}
.hero-overlay{position:absolute;inset:0;background:
  radial-gradient(700px 380px at 18% 32%, rgba(0,0,0,.45), rgba(0,0,0,.7)),
  linear-gradient(90deg, rgba(0,0,0,.6), rgba(0,0,0,.25), rgba(0,0,0,.6));opacity:calc(1 - var(--fade)*.35)}
.hero-bottom-fade{position:absolute;left:0;right:0;bottom:-1px;height:120px;background:linear-gradient(to bottom, transparent, var(--bg));z-index:2}
.hero-content{position:relative;z-index:3;height:100%;display:grid;align-content:center;gap:14px;padding: clamp(20px,5vw,64px);max-width:1100px;margin:0 auto}
.breadcrumbs{opacity:.9;font-size:.95rem}
.breadcrumbs a{color:rgba(255,255,255,.9);text-decoration:none}
.breadcrumbs span{opacity:.6;margin:0 6px}
.hero-title{font-size: clamp(2rem, 4.2vw, 3.6rem);line-height:1.1;margin:0;font-weight:800}
.hero-excerpt{max-width:60ch;opacity:.98}
.hero-meta{display:flex;gap:8px;align-items:center;opacity:.9}
.hero-meta .cat{margin-left:8px;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.2)}

/* layout */
.containerr{display:grid;grid-template-columns: 300px 1fr;gap:24px;max-width:1200px;margin:0 auto;padding: 0 clamp(16px,4vw,24px)}
.side{padding-top:28px}
main{padding-top:28px}

/* tools */
.tool-card{position:sticky;top:84px;background:var(--glass);border:1px solid var(--glass-stroke);backdrop-filter: blur(8px);border-radius:16px;padding:14px 14px 12px;margin-bottom:14px}
.tool-title{font-weight:800;margin-bottom:8px}
.toc{display:grid;gap:8px}
.toc a{padding:8px 10px;border-radius:12px;text-decoration:none;color:var(--text);border:1px solid transparent}
.toc a:hover{background:rgba(255,255,255,.08)}
.toc a.active{border-color:rgba(255,255,255,.25);background:linear-gradient(90deg, rgba(96,165,250,.28), rgba(34,211,238,.28))}
.share .share-row{display:grid;gap:8px}
.share button{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);color:var(--text);cursor:pointer}
.share button:hover{background:rgba(255,255,255,.12)}
.author .author-row{display:flex;gap:8px;align-items:center}
.author img{width:40px;height:40px;border-radius:50%;object-fit:cover}
.author .name{font-weight:700}
.author-meta{opacity:.85;margin-top:6px}

/* article */
article{max-width:760px;margin:0 auto 36px}
.lede{font-size:1.1rem;opacity:.98}
h2{font-size:1.6rem;margin:28px 0 12px;font-weight:900}
h3{font-size:1.15rem;margin:14px 0 6px;font-weight:800}
p{line-height:1.8}
ul{padding-left:18px}
ol{padding-left:18px}
.grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.check li::marker{content:"✔  "}
.steps>li{margin-bottom:6px}

/* callout */
.callout{border-radius:16px;padding:14px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);margin:14px 0}
.callout.info{background:linear-gradient(180deg, rgba(96,165,250,.15), rgba(255,255,255,.06))}
.callout.success{background:linear-gradient(180deg, rgba(52,211,153,.18), rgba(255,255,255,.06))}

/* template */
.template{border:1px solid var(--glass-stroke);border-radius:16px;overflow:hidden;background:rgba(255,255,255,.04);margin-top:8px}
.template pre{margin:0;padding:14px;line-height:1.7;white-space:pre-wrap}
.template-actions{display:flex;gap:10px;align-items:center;padding:10px;border-top:1px solid var(--glass-stroke)}
.template-actions .ghost{color:var(--text);opacity:.9;text-decoration:none}
.template-actions button{padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.08);color:var(--text);cursor:pointer}
.template-actions button:hover{background:rgba(255,255,255,.14)}

/* end actions */
.end-actions{display:flex;gap:10px;align-items:center;margin:18px 0 6px}
.btn-primary{display:inline-flex;align-items:center;gap:10px;padding:12px 16px;border-radius:999px;color:#fff;text-decoration:none;background:linear-gradient(90deg,var(--p1),var(--p2));border:1px solid rgba(255,255,255,.25)}
.ghost{padding:10px 12px;border-radius:12px;border:1px solid var(--glass-stroke);text-decoration:none;color:var(--text);background:rgba(255,255,255,.06)}

/* related */
.related{border-top:1px solid rgba(255,255,255,.08);padding-top:20px}
.related h3{font-size:1.2rem;margin:0 0 12px}
.rel-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.rel-card{position:relative;overflow:hidden;border-radius:16px;border:1px solid rgba(255,255,255,.12);text-decoration:none;color:#fff;background:#0f1629}
.rel-card img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:brightness(.9);transform:scale(1.02);transition:transform .5s}
.rel-card:hover img{transform:scale(1.06)}
.rel-card .meta{position:relative;padding:12px;background:linear-gradient(180deg, rgba(0,0,0,.0), rgba(0,0,0,.75));margin-top:110px}
.rel-card .cat{display:inline-block;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);margin-bottom:6px}
.rel-card .title{font-weight:800}

/* responsive */
@media (max-width: 1024px){
  .containerr{grid-template-columns:1fr}
  .side{order:2}
  article{max-width:680px}
}
@media (max-width:640px){
  .hero{height:54vh}
  .grid-2{grid-template-columns:1fr}
  .rel-grid{grid-template-columns:1fr}
}
    `}</style>
  );
}
